import { useState, useEffect, useMemo } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Loader2, Link2, Gamepad2, Star, X, Layers, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${getApiUrl()}/api/master`;

// Drop buckets, defined relative to the chosen Original (anchor).
const BUCKETS = [
  { key: "alternate", label: "Alternate versions", hint: "all cross-linked" },
  { key: "remaster", label: "Remasters of original" },
  { key: "remake", label: "Remakes of original" },
  { key: "sequel", label: "Sequels to original" },
  { key: "prequel", label: "Prequels to original" },
  { key: "spinoff", label: "Spin-offs of original" },
  { key: "collection_in", label: "Collections containing original" },
  { key: "contains", label: "Original is a collection containing these" },
];
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map(b => [b.key, b.label]));

export default function BulkRelateDialog({ open, onOpenChange, selectedGames, onApplied }) {
  const [anchorId, setAnchorId] = useState("");
  const [assign, setAssign] = useState({}); // gameId -> bucketKey
  const [dragId, setDragId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [repOverride, setRepOverride] = useState({}); // groupKey -> memberId
  const [expandedKey, setExpandedKey] = useState(null);

  // Collapse version groups: each group becomes ONE representative (its primary
  // version unless the admin picks another). Relating the rep creates a single
  // relation that auto-follows the whole group — so you don't link all 5
  // versions of a game as a sequel one by one.
  const collapsed = useMemo(() => {
    const map = new Map();
    for (const g of selectedGames) {
      const key = g.versionGroupId || `solo:${g.id}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(g);
    }
    return Array.from(map.entries()).map(([key, members]) => {
      const chosen = members.find(m => m.id === repOverride[key])
        || members.find(m => m.isVersionPrimary)
        || members[0];
      return { key, rep: { ...chosen, _versionCount: members.length, _members: members, _groupKey: key }, members };
    });
  }, [selectedGames, repOverride]);

  const reps = collapsed.map(c => c.rep);
  const repIdsKey = reps.map(r => r.id).join(",");

  useEffect(() => {
    if (open) {
      setAssign({});
      setDragId(null);
      setRepOverride({});
      setExpandedKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the chosen Original valid as representatives change.
  useEffect(() => {
    if (!open) return;
    if (!reps.find(r => r.id === anchorId)) setAnchorId(reps[0]?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, repIdsKey]);

  const gameById = Object.fromEntries(reps.map(g => [g.id, g]));
  const setAnchor = (id) => {
    setAnchorId(id);
    setAssign(prev => { const n = { ...prev }; delete n[id]; return n; });
  };
  const assignTo = (id, bucket) => {
    if (id === anchorId) return;
    setAssign(prev => ({ ...prev, [id]: bucket }));
  };
  const unassign = (id) => setAssign(prev => { const n = { ...prev }; delete n[id]; return n; });

  // Swap which version represents a group; migrate anchor/assignment to it.
  const swapRep = (groupKey, memberId) => {
    const c = collapsed.find(x => x.key === groupKey);
    const oldId = c?.rep.id;
    setRepOverride(prev => ({ ...prev, [groupKey]: memberId }));
    setExpandedKey(null);
    if (oldId && oldId !== memberId) {
      if (anchorId === oldId) setAnchorId(memberId);
      setAssign(prev => {
        if (!(oldId in prev)) return prev;
        const n = { ...prev }; n[memberId] = n[oldId]; delete n[oldId]; return n;
      });
    }
  };

  const pool = reps.filter(g => g.id !== anchorId && !assign[g.id]);

  const handleApply = async () => {
    const assignments = {};
    for (const [id, bucket] of Object.entries(assign)) {
      (assignments[bucket] = assignments[bucket] || []).push(id);
    }
    if (Object.keys(assignments).length === 0) {
      toast.error("Assign at least one game to a relationship");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/game-relations/bulk-assign`, { anchor_id: anchorId, assignments });
      toast.success(r.data?.message || "Relations linked");
      onApplied?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link relations");
    } finally {
      setBusy(false);
    }
  };

  const gameMeta = (g) => {
    const plats = [...new Set((g.platforms || []).map(p => String(p)))];
    const syss = (g.systems || []).map(s => String(s));
    const yr = g.releaseDate ? (String(g.releaseDate).match(/\d{4}/)?.[0] || null) : null;
    const parts = [];
    if (plats.length) parts.push(plats.join(" / "));
    if (syss.length) parts.push(syss.join(" / "));
    if (yr) parts.push(yr);
    return parts.join(" · ");
  };

  const Chip = ({ g, inZone }) => {
    const meta = gameMeta(g);
    const isGroup = g._versionCount > 1;
    const isExpanded = expandedKey === g._groupKey;
    return (
    <div className="w-full sm:w-[calc(50%-0.1875rem)] min-w-[180px]">
    <div
      draggable
      onDragStart={() => setDragId(g.id)}
      onDragEnd={() => setDragId(null)}
      data-testid={`bulk-chip-${g.id}`}
      className="flex items-start gap-1.5 p-1.5 rounded bg-white/[0.05] border border-white/10 cursor-grab active:cursor-grabbing text-xs"
    >
      <div className="w-7 h-9 shrink-0 rounded overflow-hidden bg-black/50 mt-0.5">
        {g.boxArtUrl ? <img src={g.boxArtUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Gamepad2 className="w-3 h-3 text-white/20" /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-white/90 block break-words leading-snug" data-testid={`bulk-chip-name-${g.id}`}>{g.name}</span>
        {meta && <span className="text-white/40 text-[10px] block break-words mt-0.5" data-testid={`bulk-chip-meta-${g.id}`}>{meta}</span>}
        {isGroup && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpandedKey(isExpanded ? null : g._groupKey); }}
            className="mt-1 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25"
            data-testid={`bulk-chip-versions-${g._groupKey}`}
            title="This represents all versions. Expand to target a specific one."
          >
            <Layers className="w-2.5 h-2.5" />{g._versionCount} versions
            {isExpanded ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
          </button>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 mt-0.5">
      {!inZone ? (
        <>
          <button type="button" onClick={() => setAnchor(g.id)} title="Set as original" className="text-white/30 hover:text-amber-300" data-testid={`bulk-setanchor-${g.id}`}>
            <Star className="w-3 h-3" />
          </button>
          <select
            value=""
            onChange={(e) => e.target.value && assignTo(g.id, e.target.value)}
            data-testid={`bulk-assign-select-${g.id}`}
            className="bg-white/5 border border-white/10 rounded text-white text-[10px] h-5 max-w-[90px]"
            title="Assign relationship"
          >
            <option value="" className="bg-[#0f0f0f]">assign…</option>
            {BUCKETS.map(b => <option key={b.key} value={b.key} className="bg-[#0f0f0f]">{b.label}</option>)}
          </select>
        </>
      ) : (
        <button type="button" onClick={() => unassign(g.id)} className="text-white/30 hover:text-red-400" data-testid={`bulk-unassign-${g.id}`}>
          <X className="w-3 h-3" />
        </button>
      )}
      </div>
    </div>
      {isGroup && isExpanded && (
        <div className="mt-1 ml-2 pl-2 border-l border-cyan-500/20 space-y-1" data-testid={`bulk-chip-members-${g._groupKey}`}>
          <p className="text-[9px] text-white/40 uppercase tracking-wider">Link to a specific version instead:</p>
          {g._members.map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => swapRep(g._groupKey, m.id)}
              className={`w-full text-left text-[10px] px-1.5 py-1 rounded flex items-center justify-between gap-2 ${m.id === g.id ? "bg-cyan-500/20 text-cyan-200" : "bg-white/[0.03] text-white/70 hover:bg-white/10"}`}
              data-testid={`bulk-chip-member-${m.id}`}
            >
              <span className="break-words min-w-0">{m.name}</span>
              <span className="text-white/30 shrink-0">{gameMeta(m) || ""}{m.id === g.id ? " ✓" : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-5xl max-h-[88vh] flex flex-col" data-testid="bulk-relate-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-fuchsia-400" />
            Link Related Games
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            Pick the Original, then drag each of the {reps.length} games into a relationship (or use its dropdown). Version groups show as one entry — expand a "N versions" chip to target a specific version. Everything saves in one go.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Original picker */}
          <div>
            <label className="text-white/60 text-xs uppercase tracking-wider font-medium">Original (the hub)</label>
            <select
              data-testid="bulk-relate-anchor"
              value={anchorId}
              onChange={e => setAnchor(e.target.value)}
              className="w-full mt-1 bg-white/5 border border-white/10 rounded px-2 h-9 text-white text-sm focus:outline-none focus:border-fuchsia-500/50"
            >
              {reps.map(g => <option key={g.id} value={g.id} className="bg-[#0f0f0f]">{g.name}{g._versionCount > 1 ? ` (${g._versionCount} versions)` : ""}</option>)}
            </select>
          </div>

          {/* Pool of unassigned games */}
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={() => { if (dragId) unassign(dragId); setDragId(null); }}
            data-testid="bulk-pool"
            className="p-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] min-h-[48px]"
          >
            <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Unassigned ({pool.length}) — drag into a zone below</p>
            <div className="flex flex-wrap gap-1.5">
              {pool.length === 0 ? <span className="text-white/25 text-xs italic">All games assigned</span> : pool.map(g => <Chip key={g.id} g={g} />)}
            </div>
          </div>

          {/* Drop zones */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {BUCKETS.map(b => {
              const chips = reps.filter(g => assign[g.id] === b.key);
              return (
                <div
                  key={b.key}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => { if (dragId) assignTo(dragId, b.key); setDragId(null); }}
                  data-testid={`bulk-zone-${b.key}`}
                  className={`p-2 rounded-lg border min-h-[56px] transition-colors ${dragId ? "border-fuchsia-500/50 bg-fuchsia-500/5" : "border-white/10 bg-white/[0.02]"}`}
                >
                  <p className="text-fuchsia-300/80 text-[11px] font-medium mb-1">{b.label}{b.hint ? <span className="text-white/30"> · {b.hint}</span> : null}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {chips.length === 0 ? <span className="text-white/20 text-[11px] italic">drop here</span> : chips.map(g => <Chip key={g.id} g={g} inZone />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-white/10 pt-3">
          <span className="text-white/40 text-xs">{Object.keys(assign).length} assigned · original: <span className="text-amber-300">{gameById[anchorId]?.name || "—"}</span></span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white">Cancel</Button>
            <Button data-testid="bulk-relate-apply" onClick={handleApply} disabled={busy} className="bg-fuchsia-600 hover:bg-fuchsia-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Link Relations
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
